import { EMOTES, type Emote } from '../../shared/scenes.ts';
import type { CharacterDesign } from '../../shared/characters.ts';
import type { Direction, FollowerView, NpcMarker } from '../../shared/protocol.ts';
import type { IconName } from '../../shared/icons.ts';
import { drawCharacter } from '../common/character-renderer.ts';
import { drawIcon } from '../common/icons.ts';
import type { Entity } from './entity.ts';
import { drawSpriteFrame, spriteFrame, type SpriteSource } from '../common/monster-sprite.ts';

const SKIN = '#f5d0a9';
const INK = '#2b2233';
const SHOES = '#3a2f45';

const FACING: Record<Direction, readonly [number, number]> = { down: [0, 1], up: [0, -1], left: [-1, 0], right: [1, 0] };

/** Haut de la tête du personnage par défaut, en part de la case (les étiquettes se posent au-dessus). */
const DEFAULT_HEAD = 0.3;

const drawShadow = (ctx: CanvasRenderingContext2D, px: number, py: number, s: number) => {
  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  ctx.beginPath();
  ctx.ellipse(px + s / 2, py + s * 0.9, s * 0.26, s * 0.09, 0, 0, Math.PI * 2);
  ctx.fill();
};

/**
 * Personnage : son apparence animée s'il en a une (et qu'elle est prête), sinon le personnage dessiné
 * par défaut. Renvoie le décalage vertical à appliquer aux étiquettes pour qu'elles restent au-dessus
 * de sa tête (0 pour un personnage d'une case de haut, négatif s'il est plus grand).
 */
export function drawEntity(
  ctx: CanvasRenderingContext2D, entity: Entity, design: CharacterDesign | undefined, px: number, py: number, size: number, walk: number, now: number,
): number {
  if (design) {
    const { animation, elapsed } = entity.animation(now);
    drawShadow(ctx, px, py, size);
    const top = drawCharacter(ctx, design, animation, entity.facing(now), elapsed, px, py, size, entity.outfit);
    if (top !== null) return Math.min(0, top - (py + size * DEFAULT_HEAD));
  }
  drawPlayer(ctx, entity, px, py, size, entity.sliding ? 0 : walk, !design, entity.facing(now));
  return 0;
}

/** Petit personnage procédural ; `walk` (0..1) anime le rebond et les pieds pendant un pas. */
export function drawPlayer(ctx: CanvasRenderingContext2D, entity: Pick<Entity, 'color' | 'dir'>, px: number, py: number, size: number, walk: number, shadow = true, dir = entity.dir): void {
  const s = size;
  const cx = px + s / 2;
  const bob = walk ? Math.abs(Math.sin(walk * Math.PI)) * s * 0.07 : 0;
  const base = py + s * 0.92 - bob;

  if (shadow) drawShadow(ctx, px, py, s);

  // Corps et tête
  ctx.fillStyle = entity.color;
  ctx.beginPath();
  ctx.roundRect(cx - s * 0.2, base - s * 0.42, s * 0.4, s * 0.36, s * 0.1);
  ctx.fill();
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(cx, base - s * 0.56, s * 0.2, 0, Math.PI * 2);
  ctx.fill();

  // Casquette, visière et yeux selon l'orientation (de dos : casquette seule)
  ctx.fillStyle = entity.color;
  ctx.beginPath();
  ctx.arc(cx, base - s * 0.6, s * 0.2, Math.PI, 0);
  ctx.fill();
  if (dir !== 'up') {
    const [fx] = FACING[dir];
    ctx.fillRect(cx - s * 0.14 + fx * s * 0.1, base - s * 0.63, s * 0.28, s * 0.05);
    ctx.fillStyle = INK;
    const eyes = dir === 'down' ? [-0.07, 0.07] : [fx * 0.09];
    for (const ex of eyes) ctx.fillRect(cx + ex * s - s * 0.025, base - s * 0.54, s * 0.05, s * 0.07);
  }

  // Pieds
  ctx.fillStyle = SHOES;
  const stride = walk ? Math.sin(walk * Math.PI * 2) * s * 0.04 : 0;
  ctx.fillRect(cx - s * 0.15, base - s * 0.08 + stride, s * 0.11, s * 0.08);
  ctx.fillRect(cx + s * 0.04, base - s * 0.08 - stride, s * 0.11, s * 0.08);
}

const MAX_BUBBLE_CHARS = 40;

export type LabelStyle = 'self' | 'player' | 'npc';

const PLAYER_LABEL = { background: 'rgba(20,16,30,0.65)', text: '#fff' };

/** Nom au-dessus d'un autre joueur, et bulle de chat éventuelle (pour tout le monde). */
export function drawLabel(ctx: CanvasRenderingContext2D, entity: Entity, px: number, py: number, size: number, style: LabelStyle, bubble: string | null): void {
  const s = size;
  const cx = px + s / 2;
  ctx.textAlign = 'center';
  // Seuls les autres joueurs ont leur nom au-dessus de la tête (ni le joueur lui-même, ni les PNJ).
  if (style === 'player') {
    const colors = PLAYER_LABEL;
    ctx.font = `600 ${Math.max(11, s * 0.24)}px system-ui, sans-serif`;
    const width = ctx.measureText(entity.username).width + 10;
    ctx.fillStyle = colors.background;
    ctx.beginPath();
    ctx.roundRect(cx - width / 2, py - s * 0.28, width, s * 0.3, 6);
    ctx.fill();
    ctx.fillStyle = colors.text;
    ctx.fillText(entity.username, cx, py - s * 0.06);
  }

  if (!bubble) return;
  const text = bubble.length > MAX_BUBBLE_CHARS ? `${bubble.slice(0, MAX_BUBBLE_CHARS)}…` : bubble;
  ctx.font = `${Math.max(12, s * 0.26)}px system-ui, sans-serif`;
  const bw = ctx.measureText(text).width + 16, bh = s * 0.42, by = py - s * 0.8;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.roundRect(cx - bw / 2, by - bh / 2, bw, bh, 8);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - 5, by + bh / 2);
  ctx.lineTo(cx + 5, by + bh / 2);
  ctx.lineTo(cx, by + bh / 2 + 6);
  ctx.fill();
  ctx.fillStyle = '#1d1a24';
  ctx.fillText(text, cx, by + s * 0.09);
}

const MARKERS: Record<NpcMarker, { symbol: string | { icon: IconName }; color: string }> = {
  quest: { symbol: '!', color: '#ffd640' },
  turnin: { symbol: '?', color: '#4cd988' },
  errand: { symbol: { icon: 'mail' }, color: '#7cc4ff' },
  trainer: { symbol: { icon: 'trainer' }, color: '#ff6b6b' },
};

const MARKER_INK = '#1d1a24';

/** Bulle rebondissante au-dessus d'un PNJ : quête disponible (!), à rendre (?), course à faire (lettre), dresseur (épée). */
export function drawMarker(ctx: CanvasRenderingContext2D, marker: NpcMarker, px: number, py: number, size: number, now: number): void {
  const { symbol, color } = MARKERS[marker];
  const cx = px + size / 2;
  const cy = py - size * 0.58 + Math.sin(now / 250) * size * 0.05;
  const radius = size * 0.2;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = MARKER_INK;
  ctx.lineWidth = 2;
  ctx.stroke();
  if (typeof symbol !== 'string') {
    drawIcon(ctx, symbol.icon, cx, cy, radius * 1.3, MARKER_INK);
    return;
  }
  ctx.fillStyle = MARKER_INK;
  ctx.font = `900 ${Math.round(radius * 1.3)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(symbol, cx, cy + 1);
  ctx.textBaseline = 'alphabetic';
}

/**
 * Bulle d'émotion d'une scène, au-dessus du nom : elle apparaît en grossissant, reste, puis s'efface.
 */
export function drawEmote(ctx: CanvasRenderingContext2D, emote: Emote, px: number, py: number, size: number, progress: number): void {
  const { glyph } = EMOTES[emote];
  const pop = Math.min(1, progress * 6);
  const scale = 0.6 + 0.4 * pop + (pop < 1 ? 0 : Math.sin(Math.min(1, (progress - 1 / 6) * 4) * Math.PI) * 0.08);
  const width = size * 0.62 * scale, height = size * 0.52 * scale;
  const cx = px + size / 2, bottom = py - size * 0.42;
  ctx.save();
  ctx.globalAlpha = progress < 0.82 ? 1 : 1 - (progress - 0.82) / 0.18;
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = MARKER_INK;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(cx - width / 2, bottom - height - size * 0.12, width, height, size * 0.12);
  // Pointe de la bulle, vers le personnage.
  ctx.moveTo(cx - size * 0.08, bottom - size * 0.12);
  ctx.lineTo(cx, bottom);
  ctx.lineTo(cx + size * 0.08, bottom - size * 0.12);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = emote === 'angry' || emote === 'heart' ? '#e5484d' : MARKER_INK;
  ctx.font = `900 ${Math.round(height * 0.72)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(glyph, cx, bottom - size * 0.12 - height / 2 + 1);
  ctx.restore();
}

/** Taille fixe d'une monture (surf, vol), en cases : le joueur y est assis, à une place réglée pour elle. */
export const MOUNT_SCALE = 1.4;

/**
 * Monstre qui suit un joueur (ou lui sert de monture) : son image (ou son animation), posée sur une ombre, qui sautille
 * pendant la marche et se tourne dans le sens du déplacement ; de dos quand il va vers le haut. Les chromatiques
 * scintillent. `cells` : sa taille en cases (un suiveur : celle de son espèce).
 */
export function drawFollower(ctx: CanvasRenderingContext2D, follower: FollowerView, dir: Direction, px: number, py: number, size: number, walk: number, now: number, cells = MOUNT_SCALE): void {
  const frame = spriteFrame(follower, dir === 'up' ? 'back' : 'front', now);
  if (!frame) return;
  const s = size;
  const scale = (s * cells) / Math.max(frame.sw, frame.sh);
  const w = frame.sw * scale, h = frame.sh * scale;
  const bob = walk ? Math.abs(Math.sin(walk * Math.PI)) * s * 0.1 : Math.abs(Math.sin(now / 420)) * s * 0.025;
  const cx = px + s / 2, bottom = py + s * 0.94 - bob;

  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  ctx.beginPath();
  // Un grand {monstre} a une ombre plus large.
  const shadow = Math.max(1, cells / MOUNT_SCALE);
  ctx.ellipse(cx, py + s * 0.9, s * 0.3 * shadow, s * 0.1 * Math.sqrt(shadow), 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(cx, bottom);
  // L'image regarde vers la gauche : retournée pour aller à droite (une vue de dos n'a pas de côté).
  if (dir === 'right' && !frame.back) ctx.scale(-1, 1);
  drawSpriteFrame(ctx, frame, -w / 2, -h, w, h);
  ctx.restore();
  if (follower.shiny && Math.sin(now / 180) > 0.6) {
    drawIcon(ctx, 'sparkles', cx + w * 0.3, bottom - h * 0.8, s * 0.3, '#fff6b0');
  }
}

/**
 * Monstre posé sur la carte (légendaire, décor…) : debout sur sa case, sur une ombre, il respire doucement. `cells` : sa
 * taille en cases (plus grand côté de l'image ; il déborde vers le haut et sur les côtés). Celui qui se promène se tourne
 * dans le sens de sa marche (de dos vers le haut) et sautille pendant un pas (`walk` : 0 → 1). Les chromatiques scintillent.
 */
export function drawMapMonster(ctx: CanvasRenderingContext2D, monster: SpriteSource, px: number, py: number, size: number, cells: number, dir: Direction, walk: number, now: number): void {
  const frame = spriteFrame(monster, dir === 'up' ? 'back' : 'front', now);
  if (!frame) return;
  const s = size;
  const breath = walk ? 1 : 1 + Math.sin(now / 700) * 0.025;
  const scale = (s * cells) / Math.max(frame.sw, frame.sh);
  const w = frame.sw * scale, h = frame.sh * scale * breath;
  const bob = walk ? Math.abs(Math.sin(walk * Math.PI)) * s * 0.1 : 0;
  const cx = px + s / 2, bottom = py + s * 0.96 - bob;
  const shadow = Math.max(1, Math.min(cells, w / s));

  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.beginPath();
  ctx.ellipse(cx, py + s * 0.9, s * 0.32 * shadow, s * 0.1 * Math.sqrt(shadow), 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(cx, bottom);
  // L'image regarde vers la gauche : retournée pour aller à droite (une vue de dos n'a pas de côté).
  if (dir === 'right' && !frame.back) ctx.scale(-1, 1);
  drawSpriteFrame(ctx, frame, -w / 2, -h, w, h);
  ctx.restore();
  if (monster.shiny && Math.sin(now / 180) > 0.6) drawIcon(ctx, 'sparkles', cx + w * 0.3, bottom - h * 0.8, s * 0.35, '#fff6b0');
}
